import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, AppBar, Badge, Button, Card, CardActionArea, CardContent, CardMedia, Checkbox, Chip, Container, Divider, FormControl, Grid, IconButton, InputLabel, makeStyles, MenuItem, Paper, Select, Snackbar, TextField, Toolbar, Tooltip, Typography } from '@material-ui/core';
import { Close, ExpandMore, FilterList, Launch, Link } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { UserContext } from '../../../context/UserProvider';
import { db } from '../../../config/firebase';
import moment from 'moment';
import { LinkCameraModal } from './LinkCameraModal';

const getCameraImages = process.env.REACT_APP_GET_CAMERA_IMAGES;



const useStyles = makeStyles((theme) => ({
  root: {
    background: theme.palette.background.default,
    minHeight: "100vh"
  },
  appBarSpacer: theme.mixins.toolbar,
  // AppBar secundario que se posiciona debajo del Navbar principal
  secondaryAppBar: {
    position: "sticky",
    top: theme.mixins.toolbar.minHeight, // Se posiciona debajo del AppBar principal
    zIndex: theme.zIndex.appBar - 1, // Z-index menor que el AppBar principal
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[2]
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh"
  },
  container: {
    flex: 1,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(10)
  },
  sidebar: {
    padding: theme.spacing(2),
    position: "sticky",
    top: theme.mixins.toolbar.minHeight + 64 // Debajo del AppBar secundario
  },
  summaryDrawer: { width: 360, flexShrink: 0 },
  summaryPaper: {
    width: 280,
    padding: theme.spacing(2),
    position: "sticky",
    top: theme.mixins.toolbar.minHeight + 64 // Debajo del AppBar secundario
  },
  groupHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" },
  thumbGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: theme.spacing(2), width: "100%" },
  thumbCard: { borderRadius: 16, overflow: "hidden" },
  thumbMedia: { height: 120, backgroundSize: "cover" },
  footerBar: {
    position: "fixed", left: 270, right: 0, bottom: 0, zIndex: 1000,
    background: theme.palette.background.paper, borderTop: `1px solid ${theme.palette.divider}`
  },
  footerInner: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: theme.spacing(1, 2) },
  chipsRow: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) }
}));

// Mock services (reemplazar con llamadas reales a Cloud Run)
// Nota: Mantener los nombres de propiedades para alinear con la API sugerida.


async function fetchImagesByPreset(presetId, cursor, dateFrom, dateTo, username) {
  const body = {
    username: username,
    preset_id: presetId,
  }
  const dataToRequest = JSON.stringify(body);
  // console.log("Esto es dataToRequest:",dataToRequest)
  try {
    const response = await fetch(getCameraImages, {
      method: 'POST',
      body: dataToRequest,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error('Error en la solicitud: ' + response.statusText);
    }
    const responseData = await response.json(); // Espera a que se resuelva la promesa
    // console.log(responseData);
    const items = [...responseData]
    return { items }
  } catch (error) {
    console.error(`Error en la solicitud: ${error.message}`)
  }
  // Simula 12 miniaturas por preset
  // const items = Array.from({ length: 12 }).map((_, i) => ({
  //   image_id: `${presetId}-img-${i}`,
  //   url_thumbnail: `https://picsum.photos/seed/${presetId}-${i}/300/200`,
  //   url_image: `https://picsum.photos/seed/${presetId}-${i}/1200/800`,
  //   takenAt: `2025-09-${(i % 9) + 1}T0${i % 9}:30:00Z`,
  //   anomalies: i % 4 === 0 ? ["plaga"] : i % 3 === 0 ? ["falta_riego"] : [],
  //   quality: "good"
  // }));
  // return { items, nextCursor: null };
}

// Helper para formatear en dd-mm-yyyy
const formatDDMMYYYY = (date) => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0"); // ¡Meses 0-11!
  const yyyy = date.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
};

const filter_tags = ["plaga", "falta_riego", "marchitez", "falta riego", "maleza", "estrés hídrico",
  "deficiencia nutricional", "espigado", "deficiencia", "malezas", "enfermedad"
]

export const PTZPresetsView = () => {
  const classes = useStyles();
  const { usuario, userTimezone } = useContext(UserContext);
  // Estado de filtros
  const [dateTo, setDateTo] = useState(() => {
    const hoy = new Date();
    return formatDDMMYYYY(hoy); // ej. 09-10-2025
  });

  const [dateFrom, setDateFrom] = useState(() => {
    const hace30 = new Date();
    hace30.setDate(hace30.getDate() - 30); // 30 días atrás
    return formatDDMMYYYY(hace30); // ej. 09-09-2025
  });
  const [selectedPreset, setSelectedPreset] = useState("all");
  const [selectedCamera, setSelectedCamera] = useState("all");
  const [anomalyFilter, setAnomalyFilter] = useState([]);
  const [linkedCameras, setLinkedCameras] = useState([]);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  // Datos
  const [presets, setPresets] = useState([]);
  const [imagesByPreset, setImagesByPreset] = useState({});
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState({});
  const [openSummaryFor, setOpenSummaryFor] = useState(null);
  const [error, setError] = useState("");

  // Verifica si una fecha UTC cae dentro del rango [dateFrom, dateTo] usando la zona horaria del usuario
  const isWithinUserRange = useCallback((utcISOString, fromStr, toStr, timezone) => {
    if (!utcISOString || !fromStr || !toStr || !timezone) return false;
    const imageLocal = moment.tz(utcISOString, timezone);
    const fromLocal = moment.tz(fromStr, 'YYYY-MM-DD', timezone).startOf('day');
    const toLocal = moment.tz(toStr, 'YYYY-MM-DD', timezone).endOf('day');
    return imageLocal.isBetween(fromLocal, toLocal, null, '[]');
  }, []);

  const fetchPresets = useCallback(async (params) => {

    const addr = `${usuario.username}/camcorder/presetsData`;
    const snap = await db.collection(addr).get();
    // Convierte cada doc en un objeto { id, ...data }
    const data = snap.docs.map((doc) => ({
      docId: doc.id,
      ...doc.data(),
    }));
    // console.log("Esto es data:",data)
    const sortedData = data.toSorted((a, b) => a.docId - b.docId);
    // console.log("Esto es sortedData:",sortedData)
    return sortedData;
    // Simula presets con metadatos mínimos
    // return [
    //   { id: "p1", name: "Jitomate", cropType: "Solanum lycopersicum", lastUpdated: "2025-09-22", tags: ["riego", "plaga"] },
    //   { id: "p2", name: "Chile Serrano", cropType: "Capsicum annuum", lastUpdated: "2025-09-23", tags: ["nutrición"] }
    // ];
  }, [usuario.username])

  // Cargar presets + imágenes por preset (resumen aparte para lazy)
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    (async () => {
      try {
        const p = await fetchPresets({ dateFrom, dateTo });
        if (!isMounted) return;
        setPresets(p);
        // Carga inicial de imágenes para los presets visibles
        const map = {};
        for (const pr of p) {
          const res = await fetchImagesByPreset(pr.id, null, dateFrom, dateTo, usuario.username);
          const items = (res && Array.isArray(res.items)) ? res.items : [];
          // Filtrado por rango de fechas considerando la zona horaria del usuario
          map[pr.id] = items.filter((img) =>
            isWithinUserRange(img.takenAt, dateFrom, dateTo, userTimezone)
          );
        }
        if (!isMounted) return;
        setImagesByPreset(map);
      } catch (e) {
        setError("No se pudieron cargar las imágenes.");
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [dateFrom, dateTo, usuario.username, userTimezone, isWithinUserRange, fetchPresets]);

  const visiblePresets = useMemo(() => {
    return presets.filter(pr => selectedPreset === "all" || pr.id === selectedPreset);
  }, [presets, selectedPreset]);

  async function fetchPresetSummary(presetId) {
    const doc_Addr = `${usuario.username}/camcorder/imagesSummary`;
    const doc_Snap = await db.collection(doc_Addr).doc(presetId).get();
    let data_to_return = {
      lastUpdated: "-",
      overview: "No hay resumen generado para este preset. Espere un dia para que se actualicen las imagenes.  ",
      anomalies: [
        { type: "plaga", trend: "up", severity: "medium" },
        { type: "falta_riego", trend: "down", severity: "low" }
      ],
      healthScore: presetId === 0
    }
    if (doc_Snap.exists) {
      const data = doc_Snap.data();
      data_to_return["lastUpdated"] = data.fecha_creacion;
      data_to_return["overview"] = data.resumen;
      data_to_return["healthScore"] = data.score;
      data_to_return["anomalies"] = data.anomalies;
    }
    return data_to_return;
  }

  const handleToggleSelect = (img) => {
    // Alternar selección por id
    setSelection((prev) => {
      const next = { ...prev };
      if (next[img.image_id]) delete next[img.image_id];
      else next[img.image_id] = img;
      return next;
    });
  };

  const countSelected = Object.keys(selection).length;

  const anomalyPass = useCallback((img) => {
    if (anomalyFilter.length === 0) return true;
    return anomalyFilter.some(a => img.anomalies.includes(a));
  }, [anomalyFilter]);

  const openSummary = async (presetId) => {
    // Cargar resumen LLM si no existe y abrir panel
    if (!summaries[presetId]) {
      const s = await fetchPresetSummary(presetId);
      setSummaries(prev => ({ ...prev, [presetId]: s }));
    }
    setOpenSummaryFor(presetId);
  };

  const formatDate = (timestamp) => {
    return moment(timestamp).tz(userTimezone).format('DD/MM/YYYY, HH:mm:ss');
  }

  const handleLinkSuccess = (newCamera) => {
    setLinkedCameras(prev => [...prev, newCamera]);
    setSelectedCamera(newCamera.id);
  };

  return (
    <div className={classes.root}>
      <AppBar className={classes.secondaryAppBar} color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" style={{ flex: 1 }}>Áreas de interés</Typography>
          <FormControl variant="outlined" size="small" style={{ minWidth: 160, marginRight: 8 }}>
            <TextField
              label="Desde"
              type="date"
              variant="outlined"
              size="small"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 160, marginRight: 16 }}>
            <TextField
              label="Hasta"
              type="date"
              variant="outlined"
              size="small"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180, marginRight: 8 }}>
            <InputLabel id="preset-label">Preset</InputLabel>
            <Select
              labelId="preset-label"
              value={selectedPreset}
              onChange={(e) => setSelectedPreset(e.target.value)}
              label="Preset"
            >
              <MenuItem value="all">Todos</MenuItem>
              {presets.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180, marginRight: 8 }}>
            <InputLabel id="camera-label">Cámara Enlazada</InputLabel>
            <Select
              labelId="camera-label"
              value={selectedCamera}
              onChange={(e) => setSelectedCamera(e.target.value)}
              label="Cámara Enlazada"
            >
              <MenuItem value="all">Todas</MenuItem>
              {linkedCameras.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Link />}
            onClick={() => setIsLinkModalOpen(true)}
          >
            Enlazar Cámara
          </Button>
          {/* <Tooltip title="Ejecutar análisis con seleccionadas">
            <span>
              <Button variant="contained" color="primary" disabled={countSelected === 0} startIcon={<Launch />}>
                Ejecutar análisis ({countSelected})
              </Button>
            </span>
          </Tooltip> */}
        </Toolbar>
      </AppBar>

      <div className={classes.contentWrapper}>
        <Container maxWidth="xl" className={classes.container}>
          <Grid container spacing={2}>
            {/* Sidebar de filtros adicionales */}
            <Grid item xs={12} md={3} lg={2}>
              <Paper className={classes.sidebar} elevation={1}>
                <Typography variant="subtitle1" gutterBottom><FilterList fontSize="small" /> Filtros</Typography>
                <Divider style={{ margin: "8px 0 16px" }} />
                <Typography variant="caption" gutterBottom>Filtrar por anomalías</Typography>
                <div className={classes.chipsRow}>
                  {filter_tags.map(an => (
                    <Chip
                      key={an}
                      label={an}
                      color={anomalyFilter.includes(an) ? "primary" : "default"}
                      onClick={() =>
                        setAnomalyFilter((prev) =>
                          prev.includes(an) ? prev.filter(x => x !== an) : [...prev, an]
                        )
                      }
                      size="small"
                    />
                  ))}
                </div>
              </Paper>
            </Grid>

            {/* Contenido principal: grupos por preset */}
            <Grid item xs={12} md={6} lg={7}>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Paper key={i} style={{ padding: 16, marginBottom: 12 }}>
                    <Skeleton variant="text" width={220} />
                    <Skeleton variant="rect" height={140} style={{ marginTop: 12 }} />
                  </Paper>
                ))
              ) : (
                visiblePresets.map((preset) => {
                  const imgs = (imagesByPreset[preset.id] || []).filter(anomalyPass);
                  return (
                    <Accordion key={preset.id} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <div className={classes.groupHeader}>
                          <div>
                            <Typography variant="subtitle1">{preset.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {preset.cropType} · Actualizado {preset.lastUpdated}
                            </Typography>
                            <div className={classes.chipsRow} style={{ marginTop: 6 }}>
                              {preset.tags.map(t => <Chip key={t} label={t} size="small" />)}
                            </div>
                          </div>
                          <Button size="small" onClick={(e) => { e.stopPropagation(); openSummary(preset.id); }}>
                            Ver resumen
                          </Button>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails style={{ display: "block" }}>
                        <div className={classes.thumbGrid}>
                          {imgs.map((img) => (
                            <Card key={img.image_id} className={classes.thumbCard} elevation={0}>
                              <CardActionArea onClick={() => window.open(img.url_image, "_blank")}>
                                <CardMedia className={classes.thumbMedia} image={img.url_thumbnail} title={img.image_id} />
                              </CardActionArea>
                              <CardContent style={{ padding: 8 }}>
                                <Grid container alignItems="center" justifyContent="space-between">
                                  <Grid item>
                                    {/* <Typography variant="caption">{new Date(img.takenAt).toLocaleString()}</Typography> */}
                                    <Typography variant="caption">{formatDate(img.takenAt)}</Typography>
                                  </Grid>
                                  <Grid item>
                                    {/* <Checkbox
                                  color="primary"
                                  checked={!!selection[img.image_id]}
                                  onChange={() => handleToggleSelect(img)}
                                  inputProps={{ "aria-label": "select image" }}
                                /> */}
                                  </Grid>
                                </Grid>
                                <div className={classes.chipsRow} style={{ marginTop: 4 }}>
                                  {img.anomalies.map(a => <Chip key={a} label={a} size="small" />)}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                        {imgs.length === 0 && (
                          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                            No hay imágenes que coincidan con los filtros.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Grid>

            {/* Panel de resumen LLM anclado derecha */}
            <Grid item xs={12} md={3} lg={3}>
              <Paper className={classes.summaryPaper} elevation={1}>
                {openSummaryFor ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom>Resumen · {presets.find(p => p.id === openSummaryFor)?.name}</Typography>
                    <Divider style={{ marginBottom: 12 }} />
                    {!summaries[openSummaryFor] ? (
                      <>
                        <Skeleton variant="text" />
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="rect" height={90} style={{ marginTop: 8 }} />
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" paragraph>
                          {summaries[openSummaryFor].overview}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Actualizado: {new Date(summaries[openSummaryFor].lastUpdated).toLocaleString()}
                        </Typography>
                        <Divider style={{ margin: "12px 0" }} />
                        {/* <Typography variant="caption" gutterBottom>Anomalías y tendencia</Typography>
                    <div className={classes.chipsRow}>
                      {summaries[openSummaryFor].anomalies.map(a => (
                        <Chip
                          key={a.type}
                          label={`${a.type} · ${a.trend} · ${a.severity}`}
                          size="small"
                          variant="default"
                        />
                      ))}
                    </div>
                    <Divider style={{ margin: "12px 0" }} /> */}
                        <Typography variant="caption">Health score</Typography>
                        <Typography variant="h5">{summaries[openSummaryFor].healthScore}/100</Typography>
                      </>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Selecciona “Ver resumen” en un preset para ver el análisis general del LLM.
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Footer selección */}
        {/* <div className={classes.footerBar}>
          <div className={classes.footerInner}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Badge badgeContent={countSelected} color="primary" overlap='rectangular'>
                <Typography variant="subtitle2">Seleccionadas</Typography>
              </Badge> 
              <Typography variant="caption" color="textSecondary">
                Marca imágenes para agregarlas al análisis
              </Typography>
            </div>
            <div>
              <Button
                variant="outlined"
                style={{ marginRight: 8 }}
                onClick={() => setSelection({})}
                disabled={countSelected === 0}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Launch />}
                disabled={countSelected === 0}
                onClick={() => alert(`Enviar ${countSelected} imágenes al análisis`)}
              >
                Añadir al análisis
              </Button>
            </div>
          </div>
        </div> */}

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          message={error}
          action={
            <IconButton size="small" aria-label="close" color="inherit" onClick={() => setError("")}>
              <Close fontSize="small" />
            </IconButton>
          }
        />

        <LinkCameraModal
          open={isLinkModalOpen}
          onClose={() => setIsLinkModalOpen(false)}
          onLinkSuccess={handleLinkSuccess}
        />
      </div>
    </div>
  )
}
